import { PlaidService } from "@openmoney/proto/openmoney/plaid/v1";

import { useQuery } from "@connectrpc/connect-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { PageSelector } from "../../components/PageSelector";
import { PageSizeSelector } from "../../components/PageSizeSelector";
import { TransactionRow } from "../../components/TransactionRow";

const transactionsSearchSchema = z
  .object({
    page: z.number().min(1).int(),
    limit: z.number().min(1).max(100).int(),
  })
  .partial();

export const Route = createFileRoute("/_auth/transactions")({
  validateSearch: zodValidator(transactionsSearchSchema),
  component: RouteComponent,
});

function RouteComponent() {
  const { page = 1, limit = 25 } = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data, isLoading, error } = useQuery(PlaidService.method.listTransactions, {
    limit,
    page,
  });

  return (
    <main className="page-wrap pb-8">
      <div className="mb-6">
        <h1 className="display-title text-[2rem] mb-1">Transactions</h1>
        <p className="text-[--ink-soft] text-sm m-0">Recent transaction history</p>
      </div>

      {isLoading ? (
        <div className="island-shell rounded-md p-8 text-center text-[--ink-muted] text-sm">
          Loading transactions…
        </div>
      ) : error ? (
        <div className="island-shell rounded-md p-5 text-[--red] text-sm">
          Error: {error.message}
        </div>
      ) : !data?.transactions || data.transactions.length === 0 ? (
        <div className="island-shell rounded-md py-12 px-8 text-center">
          <div className="text-[2.5rem] mb-4 opacity-30">💳</div>
          <h3 className="mb-3 text-lg">No transactions yet</h3>
          <p className="text-[--ink-soft] my-0 mx-auto mb-6 max-w-105 text-sm">
            Transactions will appear here after your accounts sync with Plaid.
          </p>
        </div>
      ) : (
        <div className="island-shell rounded-md p-5">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0">
              <thead>
                <tr className="border-b border-[--line]">
                  <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                    Date
                  </th>
                  <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                    Merchant
                  </th>
                  <th
                    colSpan={2}
                    className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono"
                  >
                    Amount
                  </th>
                  <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="[&_tr]:border-b [&_tr]:border-[--line-soft] [&_tr]:transition-colors [&_tr]:duration-150 [&_tr:hover]:bg-[--bg-soft] [&_td]:py-[0.55rem] [&_td]:px-2.5">
                {data.transactions.map((tx) => (
                  <TransactionRow key={tx.id} transaction={tx} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-[--line]">
            <PageSizeSelector
              value={limit}
              onChange={(newLimit) =>
                navigate({
                  search: { limit: newLimit, page: 1 },
                })
              }
              disabled={isLoading}
            />

            <PageSelector
              page={page}
              hasNext={data?.hasNext ?? false}
              onClickPrev={() =>
                navigate({
                  search: (prev) => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }),
                })
              }
              onClickNext={() =>
                navigate({
                  search: (prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }),
                })
              }
              disabled={isLoading}
            />
          </div>
        </div>
      )}
    </main>
  );
}
